import React from 'react';
import {
  Box,
  Container,
  Typography,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material';

export default function CookiePolicyPage() {
  const [analyticsEnabled, setAnalyticsEnabled] = React.useState(true);

  const handleSavePreferences = () => {
    // In a real app, this would save to localStorage and update analytics
    console.log('Cookie preferences saved:', { analyticsEnabled });
  };

  return (
    <Box
      sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 8 }}
    >
      <Container maxWidth="md">
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
          Cookie Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 6 }}>
          Last updated: December 31, 2025
        </Typography>

        <Box
          sx={{
            '& h3': { fontWeight: 600, mt: 4, mb: 2 },
            '& p': { mb: 2, lineHeight: 1.8 },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            What Are Cookies?
          </Typography>
          <Typography variant="body1">
            Cookies are small text files that are placed on your device when you
            visit a website. They help the website remember your preferences and
            improve your experience. SimplyPayroll uses cookies to provide and
            improve our Service.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            How We Use Cookies
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            1. Essential Cookies (Required)
          </Typography>
          <Typography variant="body1">
            These cookies are necessary for the Service to function properly.
            They cannot be disabled:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>
              <strong>Authentication:</strong> Keep you logged in securely
              across pages
            </li>
            <li>
              <strong>Security:</strong> Protect against cross-site request
              forgery (CSRF) attacks
            </li>
            <li>
              <strong>Session Management:</strong> Remember your preferences
              during a session
            </li>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            2. Analytics Cookies (Optional)
          </Typography>
          <Typography variant="body1">
            These cookies help us understand how users interact with our
            Service:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>Pages visited and time spent</li>
            <li>Features used most frequently</li>
            <li>Error messages encountered</li>
            <li>General usage patterns (anonymized)</li>
          </Typography>
          <Typography variant="body1">
            This information helps us improve the Service. You can opt-out of
            analytics cookies below.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            Cookies We Use
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              auth_token
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Type: Essential | Duration: 7 days
            </Typography>
            <Typography variant="body2">
              Stores your login session securely
            </Typography>
          </Box>

          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              csrf_token
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Type: Essential | Duration: Session
            </Typography>
            <Typography variant="body2">
              Protects against security attacks
            </Typography>
          </Box>

          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              analytics_id
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Type: Analytics | Duration: 1 year
            </Typography>
            <Typography variant="body2">
              Anonymously tracks usage patterns to help us improve the Service
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            Third-Party Cookies
          </Typography>
          <Typography variant="body1">
            We use the following third-party services that set cookies:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>
              <strong>Stripe:</strong> Processes payments securely (essential
              for checkout)
            </li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            Managing Your Cookie Preferences
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You can control cookie preferences through your browser settings or
            using the options below. Note that disabling essential cookies will
            prevent you from using the Service.
          </Typography>

          <Box
            sx={{
              bgcolor: 'primary.50',
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.200',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Your Cookie Preferences
            </Typography>

            <FormControlLabel
              control={<Switch checked disabled />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Essential Cookies (Required)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cannot be disabled - required for the Service to function
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={analyticsEnabled}
                  onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Analytics Cookies (Optional)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Help us improve the Service by tracking anonymous usage
                  </Typography>
                </Box>
              }
            />

            <Button
              variant="contained"
              onClick={handleSavePreferences}
              sx={{ mt: 2 }}
            >
              Save Preferences
            </Button>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            Browser Cookie Controls
          </Typography>
          <Typography variant="body1">
            Most browsers allow you to control cookies through their settings.
            Here's how:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>
              <strong>Chrome:</strong> Settings → Privacy and security → Cookies
            </li>
            <li>
              <strong>Firefox:</strong> Preferences → Privacy & Security →
              Cookies
            </li>
            <li>
              <strong>Safari:</strong> Preferences → Privacy → Cookies and
              website data
            </li>
            <li>
              <strong>Edge:</strong> Settings → Cookies and site permissions
            </li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            Do Not Track (DNT)
          </Typography>
          <Typography variant="body1">
            Some browsers support "Do Not Track" (DNT) signals. When we detect a
            DNT signal, we automatically disable analytics cookies (but
            essential cookies remain active for functionality).
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            Updates to This Policy
          </Typography>
          <Typography variant="body1">
            We may update this Cookie Policy to reflect changes in our practices
            or for legal reasons. We'll notify you of significant changes via
            email or a notice on our website.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            Contact Us
          </Typography>
          <Typography variant="body1">
            If you have questions about our use of cookies:
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            Email: 4YourBusinessImage@gmail.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
